import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, ChevronDown, ArrowRight, MapPin, Phone, Mail, Clock, Award } from "lucide-react";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import Reveal, { Eyebrow } from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import Testimonials from "../components/Testimonials";
import Icon from "../components/Icon";
import { findCity, SERVICES, FIRM, cityFaqs } from "../data/siteData";

function Faq({ faqs, citySlug }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => (
        <div key={i} data-testid={`faq-item-${citySlug}-${i}`} className="glass overflow-hidden rounded-2xl">
          <button
            data-testid={`faq-toggle-${citySlug}-${i}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left"
          >
            <span className="font-display text-sm font-bold text-brand-navy sm:text-base">{f.q}</span>
            <ChevronDown className={`h-5 w-5 shrink-0 text-brand-blue transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="px-6 pb-5 text-sm leading-relaxed text-brand-gray">{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}

const WHY_POINTS = (city) => [
  { title: `Local desk in ${city.name}`, desc: `Advisors who know the ${city.name} ROC, state incentives and business culture — not a remote call centre.` },
  { title: "7–10 day incorporations", desc: "Documents-to-certificate in 7–10 working days, with every registration bundled into one fixed-fee sprint." },
  { title: "40+ CA/CS professionals", desc: "Chartered accountants, company secretaries and 15+ legal advisors — 26+ years of combined team experience." },
  { title: "Fundraising-grade output", desc: "Work products built to survive investor due diligence, because 800+ clients and ₹550+ Cr of valuations taught us what breaks deals." },
];

export default function CityPage() {
  const { stateSlug, citySlug } = useParams();
  const found = findCity(stateSlug, citySlug);
  if (!found) return <Navigate to="/locations" replace />;
  const { state, city } = found;
  const faqs = cityFaqs(city.name, state.name);
  const nearby = state.cities.filter((c) => c.slug !== city.slug);
  const base = "https://compliance-capital-1.preview.emergentagent.com";

  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    name: `Complisure Counsultancy — ${city.name}`,
    url: `${base}/locations/${state.slug}/${city.slug}`,
    telephone: "+91-73510-10107",
    email: FIRM.email,
    address: {
      "@type": "PostalAddress",
      addressLocality: city.name,
      addressRegion: state.name,
      addressCountry: "IN",
    },
    areaServed: { "@type": "City", name: city.name },
    priceRange: "₹₹",
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title={`Top CA CS Firm in ${city.name} | Company Registration, GST, Virtual CFO & Fundraising Advisory`}
        description={`${city.keywords.join(", ")} — Complisure Counsultancy delivers company registration, compliance, valuation and fundraising advisory in ${city.name}, ${state.name}. Free consultation.`}
        canonical={`${base}/locations/${state.slug}/${city.slug}`}
        jsonLd={[
          orgJsonLd,
          localBusinessLd,
          faqLd,
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Locations", path: "/locations" },
            { name: state.name, path: `/locations/${state.slug}` },
            { name: city.name, path: `/locations/${state.slug}/${city.slug}` },
          ]),
        ]}
      />
      <section className="relative overflow-hidden pb-20 pt-36">
        <div className="blob blob-float left-[-8%] top-[-5%] h-96 w-96 bg-brand-blue/12" />
        <div className="blob blob-float-slow right-[-8%] top-[30%] h-80 w-80 bg-brand-sky/15" />
        <div className="dot-grid absolute inset-0 opacity-[0.1]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <nav className="flex flex-wrap items-center gap-1.5 text-xs text-brand-gray" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-brand-blue">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/locations" className="hover:text-brand-blue">Locations</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to={`/locations/${state.slug}`} className="hover:text-brand-blue">{state.name}</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-brand-navy">{city.name}</span>
            </nav>
          </Reveal>

          <div className="mt-8 grid items-start gap-12 lg:grid-cols-[1.2fr_1fr]">
            <Reveal>
              <Eyebrow>{city.tier} · {state.name}</Eyebrow>
              <h1 className="mt-3 font-display text-4xl font-extrabold leading-[1.08] tracking-[-0.02em] text-brand-navy sm:text-5xl">
                Company Registration & CA Services in <span className="text-gradient">{city.name}</span>
              </h1>
              <p className="mt-5 text-base leading-relaxed text-brand-gray sm:text-lg">
                {city.eco} Complisure Counsultancy serves {city.name} founders and businesses with the full advisory stack — company registration, GST and income tax, ROC & secretarial compliance, accounting, statutory audit, Virtual CFO services, business valuation and fundraising advisory. Whether you're incorporating your first Private Limited or preparing your next fundraising round, our {city.name} desk pairs local compliance fluency with a national network of 40+ CA/CS professionals and 15+ legal advisors.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {city.keywords.map((k) => (
                  <span key={k} className="rounded-full bg-brand-ice px-3.5 py-1.5 text-xs font-semibold text-brand-blue">{k}</span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.12} className="lg:sticky lg:top-28">
              <LeadForm city={city.name} sourcePage={`/locations/${state.slug}/${city.slug}`} />
            </Reveal>
          </div>

          {/* Services grid with local CTA */}
          <div className="mt-24">
            <Reveal>
              <h2 className="font-display text-3xl font-bold tracking-tight text-brand-navy">Our services in {city.name}</h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-brand-gray sm:text-base">
                Eleven practice areas, one engagement manager in {city.name}. Every service below includes a free initial consultation.
              </p>
            </Reveal>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {SERVICES.map((s, i) => (
                <Reveal key={s.slug} delay={(i % 3) * 0.06}>
                  <div data-testid={`city-service-${city.slug}-${s.slug}`} className="glass card-lift flex h-full flex-col rounded-3xl p-6">
                    <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-brand-sky text-white shadow-md shadow-brand-blue/25">
                      <Icon name={s.icon} className="h-5 w-5" />
                    </span>
                    <h3 className="font-display text-base font-bold text-brand-navy">{s.name.split(" &")[0]} in {city.name}</h3>
                    <p className="mt-2 flex-1 text-xs leading-relaxed text-brand-gray sm:text-sm">{s.short}</p>
                    <div className="mt-4 flex items-center gap-4">
                      <Link to={`/services/${s.slug}`} className="text-xs font-semibold text-brand-blue hover:underline">Details</Link>
                      <Link to="/contact" data-testid={`city-service-cta-${city.slug}-${s.slug}`} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-navy transition-all hover:gap-2 hover:text-brand-blue">
                        Get quote <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Why choose us */}
          <div className="mt-24">
            <Reveal>
              <h2 className="font-display text-3xl font-bold tracking-tight text-brand-navy">Why {city.name} businesses choose Complisure</h2>
            </Reveal>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {WHY_POINTS(city).map((w, i) => (
                <Reveal key={w.title} delay={i * 0.07}>
                  <div className="glass card-lift h-full rounded-3xl p-6">
                    <Award className="mb-4 h-6 w-6 text-brand-blue" />
                    <h3 className="font-display text-base font-bold text-brand-navy">{w.title}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-brand-gray sm:text-sm">{w.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Testimonials
        filterCity={city.name}
        title={`What clients say — in ${city.name} and beyond`}
        sub={`Engagements from ${city.name} and across our 31-city network.`}
      />

      <section className="bg-brand-ice/50 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <Reveal>
                <h2 className="font-display text-3xl font-bold tracking-tight text-brand-navy">FAQs — {city.name}</h2>
                <p className="mt-3 text-sm text-brand-gray sm:text-base">Straight answers to the questions {city.name} founders ask us most.</p>
              </Reveal>
              <Reveal delay={0.1} className="mt-8">
                <Faq faqs={faqs} citySlug={city.slug} />
              </Reveal>
            </div>
            <div className="space-y-6 lg:sticky lg:top-28">
              <Reveal delay={0.12}>
                <div className="glass overflow-hidden rounded-3xl">
                  <iframe
                    data-testid="city-map"
                    title={`Map of ${city.name}, ${state.name}`}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(`${city.name}, ${state.name}, India`)}&output=embed`}
                    className="h-72 w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="p-6">
                    <h3 className="font-display text-lg font-bold text-brand-navy">Complisure Counsultancy — {city.name} desk</h3>
                    <div className="mt-3 space-y-2 text-sm text-brand-gray">
                      <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" /> Serving {city.name} & {state.name} · HQ: {FIRM.address}</p>
                      <a href={`tel:${FIRM.phoneRaw}`} data-testid="city-phone" className="flex items-center gap-2 hover:text-brand-blue"><Phone className="h-4 w-4 text-brand-blue" /> {FIRM.phoneDisplay}</a>
                      <a href={`mailto:${FIRM.email}`} className="flex items-center gap-2 hover:text-brand-blue"><Mail className="h-4 w-4 text-brand-blue" /> {FIRM.email}</a>
                      <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-brand-blue" /> Mon–Sat, 9:30 AM – 7:00 PM IST</p>
                    </div>
                  </div>
                </div>
              </Reveal>
              {nearby.length > 0 && (
                <Reveal delay={0.18}>
                  <div className="glass rounded-3xl p-6">
                    <h3 className="font-display text-base font-bold text-brand-navy">Nearby cities we serve in {state.name}</h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {nearby.map((n) => (
                        <Link key={n.slug} to={`/locations/${state.slug}/${n.slug}`} className="rounded-full border border-brand-blue/15 bg-white/70 px-4 py-2 text-xs font-medium text-brand-navy transition-colors hover:bg-brand-ice hover:text-brand-blue">
                          CA services in {n.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
